/**
 * Streaming-aware typewriter text component.
 *
 * Reveals text character-by-character with a blinking teal cursor,
 * matching the ChatGPT "slow writing" style. Handles text that grows
 * over time (voice transcript streaming deltas).
 *
 * When `isStreaming` flips to `false`, remaining text is shown immediately.
 */

import { useEffect, useRef, useState } from 'react';

interface TypewriterTextProps {
  /** Full text to display — may grow as streaming deltas arrive. */
  text: string;
  /** True while voice is still streaming; false → show all text immediately. */
  isStreaming: boolean;
  /** Milliseconds per character reveal (default 20). */
  speed?: number;
}

export function TypewriterText({ text, isStreaming, speed = 20 }: TypewriterTextProps) {
  const [revealedLen, setRevealedLen] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // When streaming stops, snap to full text
  useEffect(() => {
    if (!isStreaming) {
      setRevealedLen(text.length);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [isStreaming, text.length]);

  // Character-by-character reveal while streaming
  useEffect(() => {
    if (!isStreaming) return;
    if (revealedLen >= text.length) return;

    timerRef.current = setTimeout(() => {
      setRevealedLen((prev) => Math.min(prev + 1, text.length));
    }, speed);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [revealedLen, text.length, isStreaming, speed]);

  const displayText = isStreaming ? text.slice(0, revealedLen) : text;
  const showCursor = isStreaming && revealedLen < text.length;

  return (
    <span>
      {displayText}
      {showCursor && (
        <span
          className="inline-block w-[2px] h-[0.9em] ml-0.5 align-text-bottom animate-cursor-blink"
          style={{ backgroundColor: '#00FFC8' }}
        />
      )}
    </span>
  );
}
